// deps
import React, { ReactElement, useCallback, useEffect, useMemo } from "react";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";

// interface
import { TurboStripData, TurboStripEventAction, TurboStripType } from "@core-ui/interfaces/Turbo";

// dls
import { default as Svg12 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// constants
import { STAR_ICON, STRIP_DIMENSIONS, SPLIT_PAY_ICON_DIMENSIONS } from "./constants";

// styles
import {
    Chevron,
    CtaTextWrapper,
    InfoWrapper,
    RightContent,
    Strip,
    StripContent,
    StripWrapper,
    StripWrapperParent,
} from "./styles";

// component
import TurboStripContent from "./components/TurboStripContent";
import { NewTag } from "@core-ui/widgets/new-tag";

export interface TurboStripProps {
    data: TurboStripData;
    onClickTurboStrip?: TurboStripEventAction;
    onImpressionTurboStrip?: TurboStripEventAction;
    isTurboV2?: boolean;
    isTurbo?: boolean;
}

function TurboStrip(props: TurboStripProps): ReactElement | null {
    const { onClickTurboStrip, onImpressionTurboStrip, isTurboV2, data, isTurbo = true } = props;

    const { stripMsg = "", isNewBanner = true, bannerId = "", iconUrl = "", type = TurboStripType.STATIC, cta } = data;

    const iconUrlCloudId = isEmpty(iconUrl) ? STAR_ICON : iconUrl;

    const isStaticInverse = type === TurboStripType.STATIC_INVERSE;

    const handleOnClick = useCallback(() => {
        !isEmpty(onClickTurboStrip) && onClickTurboStrip(isNewBanner, bannerId, stripMsg);
    }, [isNewBanner, bannerId, stripMsg, onClickTurboStrip]);

    const splitPayIconDimensions = useMemo(() => {
        if (type === TurboStripType.DYNAMIC_SPLIT_PAY_INFO) {
            return SPLIT_PAY_ICON_DIMENSIONS;
        }
        return STRIP_DIMENSIONS;
    }, [type]);

    const iconComp = useCallback(() => {
        if (type === TurboStripType.STATIC_INVERSE) {
            return null;
        }

        if (isNewBanner) {
            return <NewTag skipShimmer />;
        }
        return <Image {...splitPayIconDimensions} cloudinaryId={iconUrlCloudId} alt="Turbo Strip Icon" />;
    }, [iconUrlCloudId, isNewBanner, splitPayIconDimensions, type]);

    useEffect(() => {
        !isEmpty(onImpressionTurboStrip) &&
            !isEmpty(stripMsg) &&
            onImpressionTurboStrip(isNewBanner, bannerId, stripMsg);
    }, [onImpressionTurboStrip, isNewBanner, bannerId, stripMsg]);

    if (isEmpty(stripMsg)) return null;

    return (
        <StripWrapperParent type={type} onClick={handleOnClick}>
            <StripWrapper isTurboV2={isTurboV2} isTurbo={isTurbo} type={type}>
                <StripContent>
                    <InfoWrapper>
                        {iconComp()}
                        <TurboStripContent data={data} />
                    </InfoWrapper>
                    {!isEmpty(cta) ? (
                        <CtaTextWrapper
                            variant="V2.overline.neutral.bold"
                            color="text_inverse_Highest_Emphasis"
                            maxLines={1}
                        >
                            {cta.text}
                        </CtaTextWrapper>
                    ) : (
                        <RightContent>
                            <Chevron />
                            <GenericIcon Icon={Svg12} fillColor="text_High_Emphasis" />
                        </RightContent>
                    )}
                </StripContent>
                {!isStaticInverse && <Strip />}
            </StripWrapper>
        </StripWrapperParent>
    );
}

export default React.memo(TurboStrip);
